/**
 * Document preparation the loader runs beside its manifest read (layered shell surfaces, WS-6
 * §3.8): browser telemetry and the deployment brand, both best effort. They moved here from the
 * deleted `main.tsx` with their rules unchanged.
 */
import { loadDeploymentBranding } from '@zion/surface-host';
import { rememberDeploymentBranding } from '../platform/deploymentBrand.js';
import { initBrowserTelemetry } from '../telemetry/browser-otel.js';

/** The two globals `runtime-config.js` sets before any module runs. */
export interface TelemetryGlobals {
  readonly __ZION_DP_ID__?: string;
  readonly __ZION_OTLP_ENDPOINT__?: string;
}

/** What {@link prepareDocument} calls; production binds the real two. */
export interface PrepareDeps {
  readonly initTelemetry: (dpId: string, endpoint: string | undefined) => void;
  readonly loadDeploymentBrand: () => Promise<unknown>;
}

/**
 * The production bindings. The brand load is `loadDeploymentBranding` of `@zion/surface-host`
 * (`/branding.json` through the global `fetch`, no bearer, one 1500 ms deadline), and its answer is
 * remembered for the interim root's sync readers (`rememberDeploymentBranding`, WS-16d, R-125).
 */
export const DEFAULT_PREPARE_DEPS: PrepareDeps = Object.freeze({
  initTelemetry: initBrowserTelemetry,
  loadDeploymentBrand: () => loadDeploymentBranding().then(rememberDeploymentBranding),
});

/**
 * Start telemetry when `globals.__ZION_DP_ID__` is set, then load the deployment brand.
 *
 * Telemetry must never take the page down: a throw from `initTelemetry` is reported to
 * `console.error` and the boot goes on with no traces. The brand load is bounded by
 * `loadDeploymentBranding` itself; a rejection is swallowed and the brand stays neutral. Resolves
 * once the brand load settles; never rejects.
 */
export async function prepareDocument(globals: TelemetryGlobals, deps: PrepareDeps = DEFAULT_PREPARE_DEPS): Promise<void> {
  const dpId = globals.__ZION_DP_ID__;
  if (dpId) {
    try {
      deps.initTelemetry(dpId, globals.__ZION_OTLP_ENDPOINT__);
    } catch (err) {
      console.error('[shell] browser telemetry failed to initialize; continuing without it', err);
    }
  }
  try {
    await deps.loadDeploymentBrand();
  } catch {
    // The deployment brand is decoration; a failed load leaves the neutral brand.
  }
}
